import type { Member, PhoneNumber, VoiceConfig } from '@hostesa/api-client';
import {
  useAddMembership,
  useInviteMember,
  useMembers,
  usePhoneNumbers,
  useSetForwarding,
  useSetWorkingHours,
  useVoiceConfig,
} from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  StatusBadge,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@hostesa/ui';
import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { z } from 'zod';
import { useHotelContext } from '../../lib/hotel-context';

const FORWARDING_CONDITIONS = ['busy', 'no_answer', 'after_hours', 'always'] as const;
type ForwardingCondition = (typeof FORWARDING_CONDITIONS)[number];

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
type Weekday = (typeof WEEKDAYS)[number];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// ── Voice config summary ─────────────────────────────────────────────────────

function VoiceConfigSummary({ config }: { config: VoiceConfig }) {
  const { t } = useT('settings');
  const ruleCount = Object.keys(config.handoffRules ?? {}).length;

  return (
    <Card data-testid="settings-voice-summary">
      <CardHeader>
        <CardTitle>{t('voice.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-[var(--color-stone-mid)]">{t('voice.description')}</p>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase text-[var(--color-stone-mid)]">
              {t('voice.fallbackLanguage')}
            </dt>
            <dd
              data-testid="settings-voice-fallback"
              className="text-sm text-[var(--color-charcoal)]"
            >
              {config.fallbackLanguage.toUpperCase()}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-[var(--color-stone-mid)]">
              {t('voice.handoffRules')}
            </dt>
            <dd
              data-testid="settings-voice-handoff-count"
              className="text-sm text-[var(--color-charcoal)]"
            >
              {ruleCount > 0
                ? t('voice.handoffRulesConfigured', { count: ruleCount })
                : t('voice.handoffRulesNone')}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function VoiceSection({ tenantId, hotelId }: { tenantId: string; hotelId: string }) {
  const { t } = useT('settings');
  const voiceConfig = useVoiceConfig(tenantId, hotelId);

  if (voiceConfig.isLoading) {
    return <LoadingState data-testid="settings-voice-loading" title={t('voice.loading')} />;
  }
  if (voiceConfig.isError || !voiceConfig.data) {
    return (
      <ErrorState
        data-testid="settings-voice-error"
        title={t('voice.errorTitle')}
        description={t('voice.errorDescription')}
        action={
          <Button
            type="button"
            data-testid="settings-voice-retry-btn"
            onClick={() => void voiceConfig.refetch()}
          >
            {t('voice.errorTitle')}
          </Button>
        }
      />
    );
  }
  return <VoiceConfigSummary config={voiceConfig.data} />;
}

// ── Phone forwarding form ────────────────────────────────────────────────────

interface PhoneForwardingFormProps {
  tenantId: string;
  hotelId: string;
  phoneNumberId: string;
  canConfigure: boolean;
}

function PhoneForwardingForm({
  tenantId,
  hotelId,
  phoneNumberId,
  canConfigure,
}: PhoneForwardingFormProps) {
  const { t } = useT('settings');
  const setForwarding = useSetForwarding(tenantId, hotelId);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const targetSchema = z
    .string()
    .min(1, t('forwarding.targetInvalid'))
    .regex(/^\+?[0-9]{6,15}$/, t('forwarding.targetInvalid'));
  const timeoutSchema = z
    .string()
    .refine(
      (v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 0 && Number(v) <= 120),
      t('forwarding.timeoutInvalid'),
    );

  const form = useForm({
    defaultValues: {
      condition: 'no_answer' as ForwardingCondition,
      target: '',
      enabled: true,
      noAnswerTimeoutSeconds: '',
    },
    onSubmit: async ({ value }) => {
      setSaveError(null);
      setSaved(false);
      const body: Record<string, unknown> = {
        phoneNumberId,
        condition: value.condition,
        target: value.target,
        enabled: value.enabled,
      };
      if (value.noAnswerTimeoutSeconds !== '') {
        body['noAnswerTimeoutSeconds'] = Number(value.noAnswerTimeoutSeconds);
      }
      try {
        // NON-optimistic: forwarding shapes live call routing — await the backend.
        await setForwarding.mutateAsync(body as never);
        setSaved(true);
      } catch {
        setSaveError(t('forwarding.saveError'));
      }
    },
  });

  return (
    <form
      data-testid="phone-forwarding-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <form.Field
        name="condition"
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="forwarding-condition">{t('forwarding.conditionLabel')}</Label>
            <select
              id="forwarding-condition"
              data-testid="forwarding-condition-select"
              value={fieldApi.state.value}
              onChange={(e) => fieldApi.handleChange(e.target.value as ForwardingCondition)}
              disabled={!canConfigure}
              className="h-9 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-charcoal)]"
            >
              <option value="busy">{t('forwarding.conditionBusy')}</option>
              <option value="no_answer">{t('forwarding.conditionNoAnswer')}</option>
              <option value="after_hours">{t('forwarding.conditionAfterHours')}</option>
              <option value="always">{t('forwarding.conditionAlways')}</option>
            </select>
          </div>
        )}
      />

      <form.Field
        name="target"
        validators={{ onSubmit: targetSchema }}
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="forwarding-target">{t('forwarding.targetLabel')}</Label>
            <Input
              id="forwarding-target"
              data-testid="forwarding-target-input"
              type="tel"
              placeholder={t('forwarding.targetPlaceholder')}
              value={fieldApi.state.value}
              onBlur={fieldApi.handleBlur}
              onChange={(e) => fieldApi.handleChange(e.target.value)}
              aria-invalid={fieldApi.state.meta.errors.length > 0}
              disabled={!canConfigure}
            />
            {fieldApi.state.meta.errors.length > 0 && (
              <p
                data-testid="forwarding-target-error"
                role="alert"
                className="text-xs text-[var(--color-alert)]"
              >
                {String(fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0])}
              </p>
            )}
          </div>
        )}
      />

      <form.Field
        name="noAnswerTimeoutSeconds"
        validators={{ onSubmit: timeoutSchema }}
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="forwarding-timeout">{t('forwarding.timeoutLabel')}</Label>
            <Input
              id="forwarding-timeout"
              data-testid="forwarding-timeout-input"
              inputMode="numeric"
              placeholder={t('forwarding.timeoutPlaceholder')}
              value={fieldApi.state.value}
              onBlur={fieldApi.handleBlur}
              onChange={(e) => fieldApi.handleChange(e.target.value)}
              aria-invalid={fieldApi.state.meta.errors.length > 0}
              disabled={!canConfigure}
            />
            {fieldApi.state.meta.errors.length > 0 && (
              <p
                data-testid="forwarding-timeout-error"
                role="alert"
                className="text-xs text-[var(--color-alert)]"
              >
                {String(fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0])}
              </p>
            )}
          </div>
        )}
      />

      <form.Field
        name="enabled"
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="forwarding-enabled">{t('forwarding.enabledLabel')}</Label>
            <Switch
              id="forwarding-enabled"
              data-testid="forwarding-enabled-switch"
              checked={fieldApi.state.value}
              onCheckedChange={(v) => fieldApi.handleChange(v)}
              disabled={!canConfigure}
            />
          </div>
        )}
      />

      {saveError && (
        <p
          data-testid="forwarding-save-error"
          role="alert"
          className="text-sm text-[var(--color-alert)]"
        >
          {saveError}
        </p>
      )}
      {saved && (
        <p
          data-testid="forwarding-save-success"
          role="status"
          className="text-sm text-[var(--color-success)]"
        >
          {t('forwarding.saved')}
        </p>
      )}

      {canConfigure && (
        <form.Subscribe
          selector={(state) => state.isSubmitting}
          // biome-ignore lint/correctness/noChildrenProp: TanStack Form Subscribe render-prop API
          children={(isSubmitting) => (
            <Button type="submit" data-testid="forwarding-save-btn" disabled={isSubmitting}>
              {isSubmitting ? t('forwarding.saving') : t('forwarding.save')}
            </Button>
          )}
        />
      )}
    </form>
  );
}

// ── Working hours form ───────────────────────────────────────────────────────

interface WorkingHoursFormProps {
  tenantId: string;
  hotelId: string;
  canConfigure: boolean;
}

function WorkingHoursForm({ tenantId, hotelId, canConfigure }: WorkingHoursFormProps) {
  const { t } = useT('settings');
  const setWorkingHours = useSetWorkingHours(tenantId, hotelId);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const form = useForm({
    defaultValues: {
      dayOfWeek: 1 as Weekday,
      isOpen: true,
      startTime: '09:00',
      endTime: '22:00',
    },
    onSubmit: async ({ value }) => {
      setSaveError(null);
      setSaved(false);
      if (value.isOpen) {
        if (!TIME_RE.test(value.startTime) || !TIME_RE.test(value.endTime)) {
          setSaveError(t('workingHours.timeInvalid'));
          return;
        }
        if (value.endTime <= value.startTime) {
          setSaveError(t('workingHours.rangeInvalid'));
          return;
        }
      }
      const body: Record<string, unknown> = {
        dayOfWeek: value.dayOfWeek,
        isOpen: value.isOpen,
      };
      if (value.isOpen) {
        body['startTime'] = value.startTime;
        body['endTime'] = value.endTime;
      }
      try {
        // NON-optimistic: working hours gate after-hours routing — await the backend.
        await setWorkingHours.mutateAsync(body as never);
        setSaved(true);
      } catch {
        setSaveError(t('workingHours.saveError'));
      }
    },
  });

  return (
    <form
      data-testid="working-hours-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <form.Field
        name="dayOfWeek"
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="working-hours-day">{t('workingHours.dayLabel')}</Label>
            <select
              id="working-hours-day"
              data-testid="working-hours-day-select"
              value={String(fieldApi.state.value)}
              onChange={(e) => fieldApi.handleChange(Number(e.target.value) as Weekday)}
              disabled={!canConfigure}
              className="h-9 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-charcoal)]"
            >
              {WEEKDAYS.map((day) => (
                <option key={day} value={day}>
                  {t(`workingHours.days.${day}`)}
                </option>
              ))}
            </select>
          </div>
        )}
      />

      <form.Field
        name="isOpen"
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="working-hours-open">{t('workingHours.openLabel')}</Label>
            <Switch
              id="working-hours-open"
              data-testid="working-hours-open-switch"
              checked={fieldApi.state.value}
              onCheckedChange={(v) => fieldApi.handleChange(v)}
              disabled={!canConfigure}
            />
          </div>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <form.Field
          name="startTime"
          // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
          children={(fieldApi) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="working-hours-start">{t('workingHours.startLabel')}</Label>
              <Input
                id="working-hours-start"
                data-testid="working-hours-start-input"
                type="time"
                value={fieldApi.state.value}
                onBlur={fieldApi.handleBlur}
                onChange={(e) => fieldApi.handleChange(e.target.value)}
                disabled={!canConfigure}
              />
            </div>
          )}
        />
        <form.Field
          name="endTime"
          // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
          children={(fieldApi) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="working-hours-end">{t('workingHours.endLabel')}</Label>
              <Input
                id="working-hours-end"
                data-testid="working-hours-end-input"
                type="time"
                value={fieldApi.state.value}
                onBlur={fieldApi.handleBlur}
                onChange={(e) => fieldApi.handleChange(e.target.value)}
                disabled={!canConfigure}
              />
            </div>
          )}
        />
      </div>

      {saveError && (
        <p
          data-testid="working-hours-save-error"
          role="alert"
          className="text-sm text-[var(--color-alert)]"
        >
          {saveError}
        </p>
      )}
      {saved && (
        <p
          data-testid="working-hours-save-success"
          role="status"
          className="text-sm text-[var(--color-success)]"
        >
          {t('workingHours.saved')}
        </p>
      )}

      {canConfigure && (
        <form.Subscribe
          selector={(state) => state.isSubmitting}
          // biome-ignore lint/correctness/noChildrenProp: TanStack Form Subscribe render-prop API
          children={(isSubmitting) => (
            <Button type="submit" data-testid="working-hours-save-btn" disabled={isSubmitting}>
              {isSubmitting ? t('workingHours.saving') : t('workingHours.save')}
            </Button>
          )}
        />
      )}
    </form>
  );
}

// ── Phone section ────────────────────────────────────────────────────────────

function PhoneSection({
  tenantId,
  hotelId,
  canConfigure,
}: {
  tenantId: string;
  hotelId: string;
  canConfigure: boolean;
}) {
  const { t } = useT('settings');
  const numbers = usePhoneNumbers(tenantId, hotelId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (numbers.isLoading) {
    return <LoadingState data-testid="settings-phone-loading" title={t('phone.loading')} />;
  }
  if (numbers.isError) {
    return (
      <ErrorState
        data-testid="settings-phone-error"
        title={t('phone.errorTitle')}
        description={t('phone.errorDescription')}
        action={
          <Button
            type="button"
            data-testid="settings-phone-retry-btn"
            onClick={() => void numbers.refetch()}
          >
            {t('phone.errorTitle')}
          </Button>
        }
      />
    );
  }

  const list: PhoneNumber[] = numbers.data ?? [];
  if (list.length === 0) {
    return (
      <EmptyState
        data-testid="settings-phone-empty"
        title={t('phone.emptyTitle')}
        description={t('phone.emptyDescription')}
      />
    );
  }

  const activeId = selectedId ?? list[0]?.id ?? '';

  return (
    <div className="flex flex-col gap-6">
      <Card data-testid="settings-phone-numbers">
        <CardHeader>
          <CardTitle>{t('phone.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-sm text-[var(--color-stone-mid)]">{t('phone.description')}</p>
          <ul className="flex flex-col gap-1">
            {list.map((num) => (
              <li key={num.id}>
                <button
                  type="button"
                  data-testid={`settings-phone-number-${num.id}`}
                  aria-current={num.id === activeId}
                  onClick={() => setSelectedId(num.id)}
                  className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm ${
                    num.id === activeId
                      ? 'bg-[var(--color-canvas)] font-medium'
                      : 'hover:bg-[var(--color-canvas)]'
                  }`}
                >
                  <span>{num.phoneNumber}</span>
                  <StatusBadge
                    data-testid={`settings-phone-status-${num.id}`}
                    status={num.status === 'active' ? 'active' : 'inactive'}
                    label={
                      num.status === 'active' ? t('phone.statusActive') : t('phone.statusInactive')
                    }
                  />
                </button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card data-testid="settings-forwarding-card">
        <CardHeader>
          <CardTitle>{t('forwarding.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <PhoneForwardingForm
            key={`fwd:${activeId}`}
            tenantId={tenantId}
            hotelId={hotelId}
            phoneNumberId={activeId}
            canConfigure={canConfigure}
          />
        </CardContent>
      </Card>

      <Card data-testid="settings-working-hours-card">
        <CardHeader>
          <CardTitle>{t('workingHours.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <WorkingHoursForm tenantId={tenantId} hotelId={hotelId} canConfigure={canConfigure} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Invite member dialog ─────────────────────────────────────────────────────

interface InviteMemberDialogProps {
  tenantId: string;
  hotelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function InviteMemberDialog({ tenantId, hotelId, open, onOpenChange }: InviteMemberDialogProps) {
  const { t } = useT('settings');
  const inviteMember = useInviteMember(tenantId);
  const [formError, setFormError] = useState<string | null>(null);

  const emailSchema = z.string().min(1, t('invite.emailRequired')).email(t('invite.emailInvalid'));
  const passwordSchema = z
    .string()
    .min(1, t('invite.passwordRequired'))
    .min(8, t('invite.passwordTooShort'));

  const form = useForm({
    defaultValues: {
      email: '',
      fullName: '',
      password: '',
      role: 'hotel_staff' as 'hotel_admin' | 'hotel_staff',
    },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        await inviteMember.mutateAsync({
          email: value.email,
          fullName: value.fullName || undefined,
          password: value.password,
          role: value.role,
          hotelId,
        });
        onOpenChange(false);
      } catch {
        setFormError(t('invite.error'));
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-member-dialog">
        <DialogHeader>
          <DialogTitle>{t('invite.title')}</DialogTitle>
          <DialogDescription>{t('invite.description')}</DialogDescription>
        </DialogHeader>
        <form
          data-testid="invite-member-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <form.Field
            name="email"
            validators={{ onSubmit: emailSchema }}
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-email">{t('invite.emailLabel')}</Label>
                <Input
                  id="invite-email"
                  data-testid="invite-email-input"
                  type="email"
                  placeholder={t('invite.emailPlaceholder')}
                  value={fieldApi.state.value}
                  onBlur={fieldApi.handleBlur}
                  onChange={(e) => fieldApi.handleChange(e.target.value)}
                  aria-invalid={fieldApi.state.meta.errors.length > 0}
                />
                {fieldApi.state.meta.errors.length > 0 && (
                  <p
                    data-testid="invite-email-error"
                    role="alert"
                    className="text-xs text-[var(--color-alert)]"
                  >
                    {String(
                      fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0],
                    )}
                  </p>
                )}
              </div>
            )}
          />

          <form.Field
            name="fullName"
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-full-name">{t('invite.fullNameLabel')}</Label>
                <Input
                  id="invite-full-name"
                  data-testid="invite-full-name-input"
                  placeholder={t('invite.fullNamePlaceholder')}
                  value={fieldApi.state.value}
                  onBlur={fieldApi.handleBlur}
                  onChange={(e) => fieldApi.handleChange(e.target.value)}
                />
              </div>
            )}
          />

          <form.Field
            name="password"
            validators={{ onSubmit: passwordSchema }}
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-password">{t('invite.passwordLabel')}</Label>
                <Input
                  id="invite-password"
                  data-testid="invite-password-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('invite.passwordPlaceholder')}
                  value={fieldApi.state.value}
                  onBlur={fieldApi.handleBlur}
                  onChange={(e) => fieldApi.handleChange(e.target.value)}
                  aria-invalid={fieldApi.state.meta.errors.length > 0}
                />
                {fieldApi.state.meta.errors.length > 0 && (
                  <p
                    data-testid="invite-password-error"
                    role="alert"
                    className="text-xs text-[var(--color-alert)]"
                  >
                    {String(
                      fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0],
                    )}
                  </p>
                )}
              </div>
            )}
          />

          <form.Field
            name="role"
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-role">{t('invite.roleLabel')}</Label>
                <select
                  id="invite-role"
                  data-testid="invite-role-select"
                  value={fieldApi.state.value}
                  onChange={(e) =>
                    fieldApi.handleChange(e.target.value as 'hotel_admin' | 'hotel_staff')
                  }
                  className="h-9 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-charcoal)]"
                >
                  <option value="hotel_staff">{t('invite.roleHotelStaff')}</option>
                  <option value="hotel_admin">{t('invite.roleHotelAdmin')}</option>
                </select>
              </div>
            )}
          />

          {formError && (
            <p
              data-testid="invite-form-error"
              role="alert"
              className="text-sm text-[var(--color-alert)]"
            >
              {formError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              data-testid="invite-cancel-btn"
              onClick={() => onOpenChange(false)}
            >
              {t('invite.cancel')}
            </Button>
            <form.Subscribe
              selector={(state) => state.isSubmitting}
              // biome-ignore lint/correctness/noChildrenProp: TanStack Form Subscribe render-prop API
              children={(isSubmitting) => (
                <Button type="submit" data-testid="invite-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? t('invite.submitting') : t('invite.submit')}
                </Button>
              )}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Add membership dialog ────────────────────────────────────────────────────

interface AddMembershipDialogProps {
  tenantId: string;
  hotelId: string;
  member: Member | null;
  onClose: () => void;
}

function AddMembershipDialog({ tenantId, hotelId, member, onClose }: AddMembershipDialogProps) {
  const { t } = useT('settings');
  const addMembership = useAddMembership(tenantId);
  const [formError, setFormError] = useState<string | null>(null);
  const [role, setRole] = useState<'hotel_admin' | 'hotel_staff'>('hotel_staff');

  const name =
    (typeof member?.fullName === 'string' && member.fullName) ||
    (typeof member?.email === 'string' && member.email) ||
    t('members.unnamed');

  async function handleSubmit() {
    if (!member) return;
    setFormError(null);
    try {
      await addMembership.mutateAsync({ userId: member.userId, body: { role, hotelId } });
      onClose();
    } catch {
      setFormError(t('addMembership.error'));
    }
  }

  return (
    <Dialog open={member !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-membership-dialog">
        <DialogHeader>
          <DialogTitle>{t('addMembership.title')}</DialogTitle>
          <DialogDescription>{t('addMembership.description', { name })}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="add-membership-role">{t('addMembership.roleLabel')}</Label>
            <select
              id="add-membership-role"
              data-testid="add-membership-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value as 'hotel_admin' | 'hotel_staff')}
              className="h-9 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-charcoal)]"
            >
              <option value="hotel_staff">{t('addMembership.roleHotelStaff')}</option>
              <option value="hotel_admin">{t('addMembership.roleHotelAdmin')}</option>
            </select>
          </div>

          {formError && (
            <p
              data-testid="add-membership-error"
              role="alert"
              className="text-sm text-[var(--color-alert)]"
            >
              {formError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              data-testid="add-membership-cancel-btn"
              onClick={onClose}
            >
              {t('addMembership.cancel')}
            </Button>
            <Button
              type="button"
              data-testid="add-membership-submit-btn"
              disabled={addMembership.isPending}
              onClick={() => void handleSubmit()}
            >
              {addMembership.isPending ? t('addMembership.submitting') : t('addMembership.submit')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Members table ────────────────────────────────────────────────────────────

interface MembersTableProps {
  members: readonly Member[];
  canManage: boolean;
  onAddMembership: (member: Member) => void;
}

function roleLabelKey(role: Member['role']): string {
  if (role === 'hotel_admin') return 'members.roleHotelAdmin';
  if (role === 'tenant_admin') return 'members.roleTenantAdmin';
  return 'members.roleHotelStaff';
}

function MembersTable({ members, canManage, onAddMembership }: MembersTableProps) {
  const { t } = useT('settings');

  return (
    <Table data-testid="members-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('members.nameHeading')}</TableHead>
          <TableHead>{t('members.emailHeading')}</TableHead>
          <TableHead>{t('members.roleHeading')}</TableHead>
          <TableHead>{t('members.statusHeading')}</TableHead>
          <TableHead>{t('members.actionsHeading')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => {
          const name =
            (typeof member.fullName === 'string' && member.fullName) || t('members.unnamed');
          const email = (typeof member.email === 'string' && member.email) || t('members.noEmail');
          const status = member.status ?? 'active';
          return (
            <TableRow key={member.userId} data-testid={`members-row-${member.userId}`}>
              <TableCell>{name}</TableCell>
              <TableCell>{email}</TableCell>
              <TableCell>{t(roleLabelKey(member.role))}</TableCell>
              <TableCell>
                <StatusBadge
                  data-testid={`members-status-${member.userId}`}
                  status={status === 'active' ? 'active' : 'inactive'}
                  label={
                    status === 'active' ? t('members.statusActive') : t('members.statusSuspended')
                  }
                />
              </TableCell>
              <TableCell>
                {canManage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid={`members-add-membership-btn-${member.userId}`}
                    onClick={() => onAddMembership(member)}
                  >
                    {t('members.addMembership')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function MembersSection({
  tenantId,
  hotelId,
  canManage,
}: {
  tenantId: string;
  hotelId: string;
  canManage: boolean;
}) {
  const { t } = useT('settings');
  const members = useMembers(tenantId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [membershipFor, setMembershipFor] = useState<Member | null>(null);

  return (
    <Card data-testid="settings-members">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle>{t('members.title')}</CardTitle>
        {canManage ? (
          <Button
            type="button"
            data-testid="members-invite-btn"
            onClick={() => setInviteOpen(true)}
          >
            {t('members.invite')}
          </Button>
        ) : (
          <span
            data-testid="members-manage-disabled-hint"
            className="text-xs text-[var(--color-stone-mid)]"
          >
            {t('members.manageDisabledHint')}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-[var(--color-stone-mid)]">{t('members.description')}</p>

        {members.isLoading && (
          <LoadingState data-testid="members-loading" title={t('members.loading')} />
        )}
        {members.isError && (
          <ErrorState
            data-testid="members-error"
            title={t('members.errorTitle')}
            description={t('members.errorDescription')}
            action={
              <Button
                type="button"
                data-testid="members-retry-btn"
                onClick={() => void members.refetch()}
              >
                {t('members.errorTitle')}
              </Button>
            }
          />
        )}
        {members.isSuccess &&
          (members.data.length === 0 ? (
            <EmptyState
              data-testid="members-empty"
              title={t('members.emptyTitle')}
              description={t('members.emptyDescription')}
            />
          ) : (
            <MembersTable
              members={members.data}
              canManage={canManage}
              onAddMembership={setMembershipFor}
            />
          ))}
      </CardContent>

      <InviteMemberDialog
        tenantId={tenantId}
        hotelId={hotelId}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
      />
      <AddMembershipDialog
        tenantId={tenantId}
        hotelId={hotelId}
        member={membershipFor}
        onClose={() => setMembershipFor(null)}
      />
    </Card>
  );
}

// ── Settings page ────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { t } = useT('settings');
  const hotel = useHotelContext();
  const { can } = useAuth();
  const canConfigure = can('hotel:configure');
  const canManageUsers = can('user:manage');

  return (
    <div data-testid="settings-page" className="flex flex-col gap-6">
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} description={t('description')} />

      <Tabs defaultValue="voice" data-testid="settings-tabs">
        <TabsList>
          <TabsTrigger value="voice" data-testid="settings-tab-voice">
            {t('tabs.voice')}
          </TabsTrigger>
          <TabsTrigger value="phone" data-testid="settings-tab-phone">
            {t('tabs.phone')}
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="settings-tab-members">
            {t('tabs.members')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="voice" data-testid="settings-panel-voice">
          <VoiceSection tenantId={hotel.tenantId} hotelId={hotel.hotelId} />
        </TabsContent>

        <TabsContent value="phone" data-testid="settings-panel-phone">
          <PhoneSection
            tenantId={hotel.tenantId}
            hotelId={hotel.hotelId}
            canConfigure={canConfigure}
          />
        </TabsContent>

        <TabsContent value="members" data-testid="settings-panel-members">
          <MembersSection
            tenantId={hotel.tenantId}
            hotelId={hotel.hotelId}
            canManage={canManageUsers}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
